import { type RefObject, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

import {
  ApiError,
  type ArrivalEvent,
  type ArrivalsDay,
  type ArrivalsResponse,
  type OutstandingProcurementResponse,
  type OverviewResponse,
  type PfandBalanceResponse,
  type TimelineResponse,
  type TopArticleRow,
  type TopCategoryRow,
  type TopUserRow,
  api,
} from "../api/client";
import { EconomicPositionCards } from "../components/EconomicPositionCard";
import { SumupStuckBadge } from "../components/SumupStuckBadge";

const { Title, Text } = Typography;

const formatCents = (cents: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    cents / 100,
  );

/** Display name for a roster event — real_name wins when set, nickname is
 * the documented fallback (matches the backend's intent of letting the
 * frontend choose). */
const eventDisplayName = (e: ArrivalEvent): string => e.real_name ?? e.nickname;

/** Format a day in the schedule: ``DD.MM. Weekday`` with the weekday spelled
 * out in English. We force ``en`` locale on the dayjs instance because the
 * app sets the global locale to ``de`` (see App.tsx), and the spec calls
 * for English weekday names until the operator asks otherwise. */
const formatDayHeader = (isoDate: string): string =>
  dayjs(isoDate).locale("en").format("DD.MM. dddd");

interface ArrivalsWidgetProps {
  response: ArrivalsResponse | null;
  error: string | null;
}

/** Arrivals & Departures roster widget.
 *
 * Renders a Card with a fixed-height (480px) scrollable list of every
 * calendar day from the first arrival to the last departure. Each day
 * shows the date header plus arrival / departure counts; when
 * ``Expand all`` is toggled the lists of names appear as colored chips
 * beneath the header. The day matching ``response.today`` is visually
 * highlighted and auto-scrolled into view on mount via
 * ``scrollIntoView({ block: "start" })`` so operators don't have to scan
 * to locate the current row.
 *
 * The expand toggle is rendered via Card's ``extra=`` slot — that pins
 * it to the top-right of the header with no layout shift when the label
 * flips between ``Expand all`` and ``Collapse all``.
 */
const ARRIVALS_EXPANDED_KEY = "dashboard.arrivalsExpanded";

const ArrivalsWidget = ({ response, error }: ArrivalsWidgetProps) => {
  // Persist the Expand-all / Collapse-all choice across reloads.
  const [expanded, setExpanded] = useState(
    () => localStorage.getItem(ARRIVALS_EXPANDED_KEY) === "1",
  );
  const toggleExpanded = () =>
    setExpanded((prev) => {
      const next = !prev;
      localStorage.setItem(ARRIVALS_EXPANDED_KEY, next ? "1" : "0");
      return next;
    });
  const scrollRef = useRef<HTMLDivElement>(null);
  const todayRowRef = useRef<HTMLDivElement>(null);

  // Auto-scroll ONLY the inner pane to today's row — never the whole page.
  // scrollIntoView would scroll every scrollable ancestor (the page jumps);
  // instead we nudge just this container's scrollTop by the row↔container rect
  // delta so the today row lines up with the top of the 480px pane.
  useEffect(() => {
    const c = scrollRef.current;
    const r = todayRowRef.current;
    if (c && r) {
      c.scrollTop +=
        r.getBoundingClientRect().top - c.getBoundingClientRect().top;
    }
  }, [response]);

  return (
    <Card
      title="Arrivals & Departures"
      size="small"
      extra={
        <Button
          type="text"
          size="small"
          icon={expanded ? <UpOutlined /> : <DownOutlined />}
          onClick={toggleExpanded}
        >
          {expanded ? "Collapse all" : "Expand all"}
        </Button>
      }
    >
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      {response === null ? (
        <Text type="secondary">Loading…</Text>
      ) : (response.days ?? []).length === 0 ? (
        <Text type="secondary">
          No arrivals or departures recorded yet. Import roster CSV to populate
          this view.
        </Text>
      ) : (
        <div
          ref={scrollRef}
          style={{ height: 480, overflowY: "auto", paddingRight: 4 }}
        >
          {(response.days ?? []).map((day) => (
            <ArrivalsDayRow
              key={day.date}
              day={day}
              isToday={day.date === response.today}
              expanded={expanded}
              rowRef={day.date === response.today ? todayRowRef : undefined}
            />
          ))}
        </div>
      )}
    </Card>
  );
};

interface ArrivalsDayRowProps {
  day: ArrivalsDay;
  isToday: boolean;
  expanded: boolean;
  rowRef?: RefObject<HTMLDivElement>;
}

/** Single day row in the arrivals widget. Header is always shown
 * (collapsed-row format: ``DD.MM. Weekday · 3 arrivals · 1 departure`` or
 * ``DD.MM. Weekday · —`` when empty). Below the header — only when
 * ``expanded`` AND the day has events — the arrival/departure chips
 * appear. Today's row gets a blue tinted background + border so the
 * operator can spot it without scanning. */
const ArrivalsDayRow = ({
  day,
  isToday,
  expanded,
  rowRef,
}: ArrivalsDayRowProps) => {
  const hasEvents = day.arrivals.length > 0 || day.departures.length > 0;
  const countParts: string[] = [];
  if (day.arrivals.length > 0) {
    countParts.push(
      `${day.arrivals.length} arrival${day.arrivals.length === 1 ? "" : "s"}`,
    );
  }
  if (day.departures.length > 0) {
    countParts.push(
      `${day.departures.length} departure${
        day.departures.length === 1 ? "" : "s"
      }`,
    );
  }
  return (
    <div
      ref={rowRef}
      style={{
        padding: "8px 12px",
        marginBottom: 4,
        borderRadius: 4,
        border: isToday ? "1px solid #1677ff" : "1px solid transparent",
        background: isToday ? "rgba(22, 119, 255, 0.08)" : undefined,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 12,
        }}
      >
        <Text strong={isToday}>{formatDayHeader(day.date)}</Text>
        <Text type="secondary">
          {hasEvents ? countParts.join(" · ") : "—"}
        </Text>
      </div>
      {expanded && hasEvents && (
        <div style={{ marginTop: 6 }}>
          <Space size={[4, 4]} wrap>
            {day.arrivals.map((e) => (
              <Tag color="green" key={`a-${e.strichliste_user_id}`}>
                {eventDisplayName(e)}
              </Tag>
            ))}
            {day.departures.map((e) => (
              <Tag color="red" key={`d-${e.strichliste_user_id}`}>
                {eventDisplayName(e)}
              </Tag>
            ))}
          </Space>
        </div>
      )}
    </div>
  );
};

export const DashboardPage = () => {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [pfand, setPfand] = useState<PfandBalanceResponse | null>(null);
  const [outstanding, setOutstanding] =
    useState<OutstandingProcurementResponse | null>(null);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [topsAllTime, setTopsAllTime] = useState<OverviewResponse | null>(null);
  const [arrivals, setArrivals] = useState<ArrivalsResponse | null>(null);
  const [arrivalsError, setArrivalsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fire the three independent dashboard reads in parallel — the
      // page renders once all three resolve. Individual tile failures
      // surface together via the single error banner; partial-failure
      // resilience would require per-tile error state which the
      // existing overview card doesn't bother with either.
      const [
        overview,
        pfandBalance,
        outstandingProcurement,
        timelineResp,
        allTimeOverview,
      ] = await Promise.all([
        api.analyticsOverview(),
        api.getPfandBalance(),
        api.getOutstandingProcurement(),
        // Lifetime (no from/to) — the Economic-position card reads its
        // ``sums`` + per-source ``rows`` to assemble the cascade.
        api.getExpensesTimeline(),
        // All-time overview JUST for the Top tables (the default overview is
        // scoped to "since last refill"). A far-past from_date makes the scope
        // cover every transaction. Recomputes the whole overview — fine for a
        // dashboard; could become a dedicated top-N endpoint later.
        api.analyticsOverview({ from_date: "2000-01-01" }),
      ]);
      setData(overview);
      setPfand(pfandBalance);
      setOutstanding(outstandingProcurement);
      setTimeline(timelineResp);
      setTopsAllTime(allTimeOverview);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  // Arrivals is fetched separately from the overview tiles so a roster-data
  // failure doesn't blank out the financial summary (it lands in a dedicated
  // Alert above the card instead of the page-wide error banner).
  const refreshArrivals = async () => {
    setArrivalsError(null);
    try {
      const resp = await api.getDashboardArrivals();
      setArrivals(resp);
    } catch (err) {
      setArrivalsError(err instanceof ApiError ? err.message : String(err));
    }
  };

  useEffect(() => {
    void refresh();
    void refreshArrivals();
  }, []);

  const articleCols: ColumnsType<TopArticleRow> = [
    { title: "Article", dataIndex: "name" },
    { title: "Sold", dataIndex: "qty_sold", align: "right" },
    {
      title: "Revenue",
      dataIndex: "revenue_cents",
      align: "right",
      render: (v: number) => formatCents(v),
    },
  ];
  const categoryCols: ColumnsType<TopCategoryRow> = [
    { title: "Category", dataIndex: "category_name" },
    {
      title: "Revenue",
      dataIndex: "revenue_cents",
      align: "right",
      render: (v: number) => formatCents(v),
    },
  ];
  const userCols: ColumnsType<TopUserRow> = [
    { title: "User", dataIndex: "nickname" },
    {
      title: "Email",
      dataIndex: "email",
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "Spend",
      dataIndex: "spend_cents",
      align: "right",
      render: (v: number) => formatCents(v),
    },
  ];

  return (
    <Card loading={loading}>
      <Space align="center" style={{ marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>
          Overview
        </Title>
        {/* Paid-but-unsettled SumUp card payments being finalized by the
            poller — links to the filtered transactions view. Hidden at 0. */}
        <SumupStuckBadge />
      </Space>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(null)}
        />
      )}
      {data && (
        <>
          <div style={{ marginBottom: 24 }}>
            <EconomicPositionCards timeline={timeline} pfand={pfand} />
          </div>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24}>
              <ArrivalsWidget response={arrivals} error={arrivalsError} />
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={8}>
              <Card title="Top articles by revenue (all time)" size="small">
                <Table<TopArticleRow>
                  columns={articleCols}
                  dataSource={(topsAllTime ?? data).top_articles}
                  rowKey="article_id"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card title="Top categories (all time)" size="small">
                <Table<TopCategoryRow>
                  columns={categoryCols}
                  dataSource={(topsAllTime ?? data).top_categories}
                  rowKey={(r) =>
                    `${r.category_id ?? "none"}-${r.category_name}`
                  }
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card title="Top users by spend (all time)" size="small">
                <Table<TopUserRow>
                  columns={userCols}
                  dataSource={(topsAllTime ?? data).top_users}
                  rowKey={(r) => `${r.strichliste_user_id}`}
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>

          {/* Snapshot stats + Pfand + Outstanding invoices — moved to the foot. */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="Active users" value={data.active_users} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Outstanding user balance"
                  value={data.total_user_balance_cents / 100}
                  precision={2}
                  suffix="€"
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Bank balance"
                  value={data.bank_balance_cents / 100}
                  precision={2}
                  suffix="€"
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="Transactions"
                  value={data.transactions_in_scope}
                />
              </Card>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            {pfand && (
              <Col xs={24} sm={12} md={8}>
                <Card title="Pfand position" size="small">
                  <Space size="large" wrap>
                    <Statistic
                      title="In"
                      value={pfand.received_cents / 100}
                      precision={2}
                      suffix="€"
                    />
                    <Statistic
                      title="Out"
                      value={pfand.returned_cents / 100}
                      precision={2}
                      suffix="€"
                    />
                    <Statistic
                      title="Net"
                      value={pfand.net_outstanding_cents / 100}
                      precision={2}
                      suffix="€"
                    />
                  </Space>
                </Card>
              </Col>
            )}
            {outstanding && (
              <Col xs={24} sm={12} md={8}>
                {/* TODO: the procurement list page doesn't currently filter
                    "received AND no paid_in_cash flag set" — the closest match
                    is state=received via the namespaced URL state
                    (`p_state=received`). */}
                <Link to="/stock/procurement?p_state=received">
                  <Card title="Outstanding invoices" size="small" hoverable>
                    <Space size="large" wrap>
                      <Statistic
                        title="Total"
                        value={outstanding.total_cents / 100}
                        precision={2}
                        suffix="€"
                      />
                      <Statistic
                        title="Refills"
                        value={outstanding.refill_count}
                      />
                    </Space>
                  </Card>
                </Link>
              </Col>
            )}
          </Row>
        </>
      )}
    </Card>
  );
};
